import { useCallback } from 'react';
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

import { useInView, useSeam } from '@/hooks';
import { cn } from '@/utils';

import styles from './Band.module.css';

/** The counter side's ground. `open` and `close` bookend the page. */
export type BandTone = 'flat' | 'deep' | 'darkest' | 'open' | 'close';

export interface BandProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  /**
   * Where the seam sits, as a percentage of the band's width, when the band
   * enters the viewport and once it has settled. One band's `seamTo` is the
   * next band's `seamFrom`, which is what keeps the line continuous.
   */
  seamFrom: number;
  seamTo: number;
  tone?: BandTone;
  /** A soft light behind the counter side. */
  glow?: boolean;
  /** The left side: flat, grey, right-aligned. The thing being replaced. */
  record: ReactNode;
  /** The right side: deep, lit, left-aligned. The thing that replaces it. */
  counter: ReactNode;
  /**
   * Rendered over both halves, so it can be positioned against the seam with
   * `left: var(--seam, var(--seam-to))`. Anything that straddles the seam is
   * Humrah — that is the rule the overlay exists to serve.
   */
  overlay?: ReactNode;
}

/**
 * One band of the page, split into the record and the counter.
 *
 * The band sets `data-revealed` on its own element, and a section may hang its
 * own sequencing off that attribute — see `ProblemBand`, whose fork draws
 * itself once the band arrives. Hide only on `[data-revealed='false']`, never
 * on the absence of the attribute, or a band the observer never reports stays
 * blank.
 *
 * Below 1024px there is no room for two columns, so the seam rotates: the
 * record sits above, the counter below, and the thread runs horizontally
 * between them. The record's content is shorter further down the page, so the
 * grey side still gives up ground as you scroll — the arc survives the
 * rotation without needing a second set of measurements.
 */
export function Band({
  seamFrom,
  seamTo,
  tone = 'flat',
  glow = false,
  record,
  counter,
  overlay,
  className,
  ...props
}: BandProps) {
  const seamRef = useSeam<HTMLElement>(seamFrom, seamTo);
  const { ref: viewRef, isInView } = useInView<HTMLElement>({ threshold: 0.08 });

  // Two hooks, one element. `useInView` fails open — absent observer, a skipped
  // intersection or a fast scroll all resolve to visible — so the reveal below
  // can never leave a band blank.
  const setRefs = useCallback(
    (node: HTMLElement | null) => {
      seamRef.current = node;
      viewRef.current = node;
    },
    [seamRef, viewRef],
  );

  return (
    <section
      ref={setRefs}
      data-revealed={isInView}
      className={cn(styles.band, styles[tone], className)}
      style={{ '--seam-to': `${seamTo}%` } as CSSProperties}
      {...props}
    >
      <div className={styles.record}>{record}</div>
      {/* The seam itself. Decorative: the two sides are already labelled. */}
      <span className={styles.thread} aria-hidden="true" />
      <div className={styles.counter}>
        {glow ? <span className={styles.bloom} aria-hidden="true" /> : null}
        {counter}
      </div>
      {overlay}
    </section>
  );
}

export interface SeamStepProps {
  /** The seam position of the band above and the band below. */
  from: number;
  to: number;
  /** The ground of the band below, so the step lands on the right colour. */
  tone?: BandTone;
}

/**
 * The diagonal between two bands, where the seam changes position.
 *
 * With the scroll animation running the two sides already agree at a boundary
 * and this only bridges the last pixel or two. Without it — no JavaScript, or
 * reduced motion — every band sits at its finished position and this is the
 * whole of the migration, which is why it is drawn rather than assumed.
 */
export function SeamStep({ from, to, tone = 'flat' }: SeamStepProps) {
  // The step animates its lower point in step with the band below it, so the
  // diagonal is never steeper than the migration it is bridging.
  const ref = useSeam<HTMLDivElement>(from, to);

  return (
    <div
      ref={ref}
      className={cn(styles.step, styles[tone])}
      style={{ '--step-from': `${from}%`, '--step-to': `${to}%` } as CSSProperties}
      aria-hidden="true"
    />
  );
}
